import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import BusinessIcon from '@mui/icons-material/Business';
import moment from 'moment';
import Swal from 'sweetalert2';
import { ModernPageContainer, ModernPageHeader } from '../components/ModernTable';
import LoadingIcon from '../../items/buttons/LoadingIcon';
import request from '../../../services/Request';

const statusColor = (status) => {
  if (status === 'success') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'missing') return 'warning';
  return 'default';
};

const statusLabel = (status) => {
  if (status === 'missing') return 'no file';
  if (status === 'no_data') return 'no sales';
  return status;
};

const statusHint = (status) => {
  if (status === 'missing') return 'The job never ran for this day, so no file was generated or sent.';
  if (status === 'no_data') return 'The job ran but there were no transactions that day, so there is no file to send.';
  if (status === 'failed') return 'The job ran and failed. See the error column.';
  if (status === 'success') return 'File was generated and uploaded to the client.';
  return '';
};

export default function ClientFileSync() {
  const theme = useTheme();

  const [tasks, setTasks] = useState([]);
  const [taskName, setTaskName] = useState('WHSmith');
  // Defaults to yesterday, since these files are always generated a day later
  const [fileDate, setFileDate] = useState(moment().subtract(1, 'day').toDate());
  const [upload, setUpload] = useState(true);

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  // `${taskName}|${YYYY-MM-DD}` of the row currently running, null when idle
  const [runningKey, setRunningKey] = useState(null);
  const running = runningKey !== null;
  const [result, setResult] = useState(null);

  // Filters for the sync history table
  const [filterClient, setFilterClient] = useState('all');
  const [filterTask, setFilterTask] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');


  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await request({
        url: '/integrationSync/history?days=60',
        method: 'GET',
      });
      setHistory(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const loadTasks = async () => {
      try {
        const res = await request({ url: '/integrationSync/tasks', method: 'GET' });
        setTasks(res.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    loadTasks();
    loadHistory();
  }, [loadHistory]);

  // taskName -> { label, client }; history rows only carry taskName
  const taskMap = useMemo(() => {
    const map = {};
    tasks.forEach((task) => {
      map[task.taskName] = task;
    });
    return map;
  }, [tasks]);

  const clientOf = useCallback((name) => taskMap[name]?.client || name, [taskMap]);

  // Every integration company, de-duplicated
  const clients = useMemo(() => {
    const set = new Set(tasks.map((task) => task.client).filter(Boolean));
    history.forEach((row) => {
      if (taskMap[row.taskName]?.client) set.add(taskMap[row.taskName].client);
    });
    return Array.from(set).sort();
  }, [tasks, history, taskMap]);

  // Integrations belonging to the selected company
  const tasksOfClient = useMemo(
    () => (filterClient === 'all' ? tasks : tasks.filter((task) => task.client === filterClient)),
    [tasks, filterClient]
  );

  const filtersActive =
    filterClient !== 'all' || filterTask !== 'all' || filterStatus !== 'all' || search.trim() !== '';

  const filteredHistory = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return history.filter((row) => {
      if (filterClient !== 'all' && clientOf(row.taskName) !== filterClient) return false;
      if (filterTask !== 'all' && row.taskName !== filterTask) return false;
      if (filterStatus !== 'all' && row.status !== filterStatus) return false;
      if (keyword) {
        const haystack = [
          row.taskName,
          clientOf(row.taskName),
          taskMap[row.taskName]?.label,
          row.status,
          statusLabel(row.status),
          row.errorMessage,
          moment.utc(row.syncDate).format('YYYY-MM-DD'),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(keyword)) return false;
      }
      return true;
    });
  }, [history, filterClient, filterTask, filterStatus, search, clientOf, taskMap]);

  // Days that need a human: no file generated, or the run failed
  const problemCount = useMemo(
    () => history.filter((row) => row.status === 'missing' || row.status === 'failed').length,
    [history]
  );

  const clearFilters = () => {
    setFilterClient('all');
    setFilterTask('all');
    setFilterStatus('all');
    setSearch('');
  };

  const downloadFile = (file) => {
    const blob = new Blob([file.content], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', file.filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Shared by the form above and the per-row buttons in the history table
  const runSync = async (task, formatted) => {
    const confirm = await Swal.fire({
      title: 'Re-generate client file?',
      html: `<b>${task}</b> file for <b>${formatted}</b><br/>${
        upload
          ? 'The file will be generated, downloaded to your computer <b>and uploaded to the client SFTP server</b>.'
          : 'The file will only be generated and downloaded to your computer (<b>no upload</b> to the client).'
      }`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: upload ? 'Generate & Send' : 'Generate only',
    });
    if (!confirm.isConfirmed) return;

    setRunningKey(`${task}|${formatted}`);
    setResult(null);
    try {
      const res = await request({
        url: '/integrationSync/rerun',
        method: 'POST',
        data: { taskName: task, fileDate: formatted, upload },
      });

      setResult(res.data);

      // Download to the browser automatically
      (res.data.files || []).forEach(downloadFile);

      Swal.fire({
        text: res.data.message,
        icon: res.data.files?.length ? 'success' : 'info',
      });
      loadHistory();
    } catch (err) {
      const message = err?.response?.data?.message || err.message;
      Swal.fire({
        title: 'Failed',
        html: `${message}${
          upload
            ? '<br/><br/>If the upload to the client failed, turn off <b>Send to client server</b> and run again to at least download the file.'
            : ''
        }`,
        icon: 'error',
      });
    } finally {
      setRunningKey(null);
    }
  };

  const handleRun = () => {
    if (!fileDate) {
      return Swal.fire({ text: 'Please select the file date.', icon: 'warning' });
    }
    return runSync(taskName, moment(fileDate).format('YYYY-MM-DD'));
  };

  return (
    <ModernPageContainer>
      {(loading || running) && <LoadingIcon />}

      <ModernPageHeader
        title="Client File Sync"
        subtitle="Re-generate and re-send a missing daily integration file to a client's server"
        icon={SyncAltIcon}
        buttons={[
          {
            text: 'Refresh history',
            onClick: loadHistory,
            icon: RefreshIcon,
            variant: 'outlined',
          },
        ]}
      />

      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Re-run an integration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Pick the date shown on the missing file. The file is rebuilt from the transactions of that
          day, downloaded to your computer, and (optionally) uploaded again to the client's SFTP
          server. The history below lists every day in the range — days marked{' '}
          <b>no file</b> never ran and were never sent.
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Integration"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
            >
              {tasks.map((task) => (
                <MenuItem key={task.taskName} value={task.taskName}>
                  {task.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={fileDate}
                onChange={(value) => setFileDate(value)}
                label="File date"
                format="MM/dd/yyyy"
                maxDate={moment().subtract(1, 'day').toDate()}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch checked={upload} onChange={(e) => setUpload(e.target.checked)} />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <CloudUploadIcon fontSize="small" />
                  <Typography variant="body2">Send to client server</Typography>
                </Box>
              }
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SyncAltIcon />}
              disabled={running}
              onClick={handleRun}
            >
              {running ? 'Running...' : 'Run'}
            </Button>
          </Grid>
        </Grid>

        {result && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Alert severity={result.files?.length ? 'success' : 'info'} sx={{ mb: 2 }}>
              {result.message} {result.transactions != null && `(${result.transactions} transactions)`}
            </Alert>
            {(result.files || []).map((file) => (
              <Box
                key={file.filename}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 1.5,
                  mb: 1,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                }}
              >
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {file.filename}
                </Typography>
                <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadFile(file)}>
                  Download again
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      <Paper
        sx={{
          p: 3,
          mt: 3,
          borderRadius: 3,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Sync history (last 60 days)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {problemCount > 0 && (
              <Chip
                size="small"
                color="warning"
                label={`${problemCount} day(s) with no file`}
                onClick={() => {
                  setFilterStatus('missing');
                  setFilterClient('all');
                  setFilterTask('all');
                  setSearch('');
                }}
              />
            )}
            <Typography variant="body2" color="text.secondary">
              Showing {filteredHistory.length} of {history.length} day(s)
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Company"
              value={filterClient}
              onChange={(e) => {
                setFilterClient(e.target.value);
                // The selected integration may not belong to the new company
                setFilterTask('all');
              }}
            >
              <MenuItem value="all">All companies</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client} value={client}>
                  {client}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Integration"
              value={filterTask}
              onChange={(e) => setFilterTask(e.target.value)}
            >
              <MenuItem value="all">All integrations</MenuItem>
              {tasksOfClient.map((task) => (
                <MenuItem key={task.taskName} value={task.taskName}>
                  {task.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="missing">No file (never ran)</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="no_data">No sales</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={9} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Company, integration, date or error"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3} md={1}>
            <Tooltip title="Clear filters">
              <span>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={!filtersActive}
                  onClick={clearFilters}
                  sx={{ minWidth: 0 }}
                >
                  <FilterAltOffIcon fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File date</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Integration</TableCell>
                <TableCell>Our record</TableCell>
                <TableCell>Attempts</TableCell>
                <TableCell>Last attempt</TableCell>
                <TableCell>Error</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHistory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary">
                      {history.length === 0
                        ? 'No sync records yet.'
                        : 'No sync records match the current filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filteredHistory.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {moment.utc(row.syncDate).format('YYYY-MM-DD')}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{clientOf(row.taskName)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={taskMap[row.taskName]?.label || ''}>
                      <span>{row.taskName}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={statusHint(row.status)}>
                      <Chip
                        size="small"
                        label={statusLabel(row.status)}
                        color={statusColor(row.status)}
                        variant={row.status === 'no_data' ? 'outlined' : 'filled'}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.attempts}</TableCell>
                  <TableCell>
                    {row.lastAttempt ? moment(row.lastAttempt).format('YYYY-MM-DD HH:mm') : '-'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="caption" color="error" sx={{ wordBreak: 'break-word' }}>
                      {row.errorMessage || ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {(() => {
                      const dateStr = moment.utc(row.syncDate).format('YYYY-MM-DD');
                      const isRunning = runningKey === `${row.taskName}|${dateStr}`;
                      // A day with no file or a failed run is the reason you are on this page
                      const needsAction = row.status === 'missing' || row.status === 'failed';
                      return (
                        <Button
                          size="small"
                          variant={needsAction ? 'contained' : 'outlined'}
                          color={needsAction ? 'warning' : 'inherit'}
                          disabled={running}
                          startIcon={
                            isRunning ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <SyncAltIcon fontSize="small" />
                            )
                          }
                          onClick={() => runSync(row.taskName, dateStr)}
                        >
                          {isRunning ? 'Running' : needsAction ? 'Sync' : 'Re-send'}
                        </Button>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </ModernPageContainer>
  );
}
